import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { PrimaryButton as PrimaryButtonBase } from 'components/misc/Buttons.js';
import { useDispatch, useSelector } from 'react-redux';
import { getMensajeBloqueoMezclaCarrito, handleAddToCart } from 'utils/carritoUtils';
import { obtenerAdicionales } from 'scripts/obtenerAdicionales';
import { toast } from 'react-toastify';

const Content = tw.div`flex flex-col items-center`;
const Details = tw.div`w-full`;
const Title = tw.h1`text-3xl font-bold text-primary-500 mb-4 text-center`;
const Description = tw.p`text-lg text-gray-700 mb-6 leading-relaxed text-center`;
const PriceContainer = tw.div`mb-4 text-center`;

const OriginalPrice = styled.p`
	${tw`text-lg text-gray-500 line-through mb-1`}
`;

const FinalPrice = styled.p`
	${tw`text-2xl font-semibold mb-2`}
`;

const SavingsAmount = styled.p`
	${tw`text-lg font-bold text-green-600 mb-2`}
`;

const PriceLabel = styled.span`
	${tw`text-sm text-gray-600 uppercase tracking-wide`}
`;
const ProductList = tw.div`mt-6 w-full`;
const ProductItem = tw.div`flex items-center p-4 border-b border-gray-300`;
const ProductImage = styled.div`
	${tw`w-20 h-20 bg-center bg-cover rounded-md mr-4`}
	${(props) => `background-image: url("${props.imageSrc}");`}
`;
const ProductDetails = tw.div`flex-1`;
const ProductTitle = tw.p`text-gray-900 font-semibold`;
const ProductDescription = tw.p`text-gray-600`;
const ProductQuantity = tw.span`text-sm text-gray-600`;
const ButtonContainer = tw.div`flex justify-center mt-4`;
const QuantityContainer = tw.div`flex justify-center mt-4`;

const QuantityButton = styled.button`
	${tw`px-3 py-1 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300`}
`;

const QuantityInput = styled.input`
	${tw`w-12 text-center border border-gray-300 rounded-md`}
`;

const PrimaryButton = styled(PrimaryButtonBase)`
	${tw`px-6 py-3 bg-primary-500 text-white font-medium rounded-lg shadow-lg`}
	&:hover {
		${tw`bg-primary-700`}
	}
`;

const SelectContainer = tw.div`flex flex-col mt-2`;
const SelectLabel = tw.label`text-xs font-medium text-gray-600 mb-1`;
const Select = styled.select`
	${tw`px-2 py-1 border border-gray-300 rounded-md bg-white text-gray-700 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
`;

const AdicionalesContainer = tw.div`mt-2 w-full`;
const PrecioAdicional = tw.span`text-xs text-green-600 font-medium ml-2`;
const ErrorMessage = tw.p`text-red-500 text-sm text-center mt-2`;

const Pack = ({ pack }) => {
	const dispatch = useDispatch();
	const [cantidad, setCantidad] = useState(1);
	const [productosAdicionales, setProductosAdicionales] = useState({});
	const [adicionalesSeleccionados, setAdicionalesSeleccionados] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [precioTotalConAdicionales, setPrecioTotalConAdicionales] = useState(0);
	const [productosExpandidos, setProductosExpandidos] = useState([]);

	const token = sessionStorage.getItem('token');
	const user = useSelector((state) => state.usuario);
	const cantidadProductosCarrito = useSelector((state) => state.carrito.cantidadProductos);
	const cartItems = useSelector((state) => state.carrito.items || []);
	let userId = null;
	if (user.isLoggedIn) {
		userId = user.userInfo.id;
	}

	// Use the new pricing structure from backend API
	const precioCalculado = pack.precio_calculado || 0;
	const precioFinal = pack.precio_mostrar || pack.precio || 0;
	const ahorro = pack.ahorro || 0;

	// Función para expandir productos con adicionales
	const expandirProductos = (productos, adicionalesData) => {
		const productosExpandidos = [];

		productos.forEach((producto) => {
			const adicionales = adicionalesData[producto.id] || [];
			const tieneAdicionales = adicionales.length > 0;

			if (tieneAdicionales && producto.cantidad > 1) {
				// Si tiene adicionales y cantidad > 1, crear una instancia por cada cantidad
				for (let i = 0; i < producto.cantidad; i++) {
					productosExpandidos.push({
						...producto,
						instancia: i,
						key: `${producto.id}-${i}`,
						mostrarCantidad: false,
					});
				}
			} else {
				// Si no tiene adicionales o cantidad es 1, mantener como está
				productosExpandidos.push({
					...producto,
					instancia: 0,
					key: `${producto.id}-0`,
					mostrarCantidad: !tieneAdicionales,
				});
			}
		});

		return productosExpandidos;
	};

	// Cargar adicionales para cada producto del pack
	useEffect(() => {
		const cargarAdicionales = async () => {
			if (!pack.productos || pack.productos.length === 0) {
				setLoading(false);
				return;
			}

			try {
				const adicionalesData = {};
				const seleccionadosIniciales = {};

				// Primero cargar todos los adicionales
				for (const producto of pack.productos) {
					try {
						const adicionales = await obtenerAdicionales(producto.id);
						adicionalesData[producto.id] = adicionales;
					} catch (error) {
						console.error(`Error al cargar adicionales para producto ${producto.id}:`, error);
						adicionalesData[producto.id] = [];
					}
				}

				// Expandir productos según cantidad y adicionales
				const expandidos = expandirProductos(pack.productos, adicionalesData);

				// Inicializar selecciones para cada instancia
				expandidos.forEach((producto) => {
					const adicionales = adicionalesData[producto.id] || [];
					const key = producto.key;

					if (adicionales.length === 0) {
						seleccionadosIniciales[key] = 0;
					} else {
						seleccionadosIniciales[key] = null;
					}
				});

				setProductosAdicionales(adicionalesData);
				setProductosExpandidos(expandidos);
				setAdicionalesSeleccionados(seleccionadosIniciales);
				calcularPrecioTotal(precioFinal, seleccionadosIniciales, adicionalesData, expandidos);
			} catch (error) {
				console.error('Error general al cargar adicionales:', error);
				setError('Error al cargar opciones de productos');
			} finally {
				setLoading(false);
			}
		};

		cargarAdicionales();
	}, [pack.productos, precioFinal]);

	// Calcular precio total incluyendo adicionales
	const calcularPrecioTotal = (precioBase, selecciones, adicionalesData, expandidos) => {
		let costoAdicionales = 0;

		Object.entries(selecciones).forEach(([key, adicionalId]) => {
			if (adicionalId && adicionalId !== 0) {
				// Extraer el productoId de la key (formato: "productoId-instancia")
				const productoId = key.split('-')[0];
				const adicionales = adicionalesData[productoId] || [];
				const adicionalSeleccionado = adicionales.find((a) => a.id === parseInt(adicionalId));
				if (adicionalSeleccionado) {
					costoAdicionales += adicionalSeleccionado.costoAdicional || 0;
				}
			}
		});

		setPrecioTotalConAdicionales(precioBase + costoAdicionales);
	};

	// Manejar cambio de adicional seleccionado
	const handleAdicionalChange = (key, adicionalId) => {
		const nuevasSelecciones = {
			...adicionalesSeleccionados,
			[key]: adicionalId === '' ? null : parseInt(adicionalId),
		};

		setAdicionalesSeleccionados(nuevasSelecciones);
		calcularPrecioTotal(precioFinal, nuevasSelecciones, productosAdicionales, productosExpandidos);
		setError(''); // Limpiar errores al hacer una selección
	};

	// Validar que todos los productos con adicionales tengan una selección
	const validarSelecciones = () => {
		for (const productoExpandido of productosExpandidos) {
			const key = productoExpandido.key;
			const adicionales = productosAdicionales[productoExpandido.id] || [];
			const adicionalId = adicionalesSeleccionados[key];

			if (adicionales.length > 0 && (adicionalId === null || adicionalId === '')) {
				return false;
			}
		}
		return true;
	};

	const handleAgregarACarrito = () => {
		const mensajeBloqueo = getMensajeBloqueoMezclaCarrito(cartItems, 'pack');
		if (mensajeBloqueo) {
			toast.info(mensajeBloqueo);
			return;
		}

		// Validar selecciones antes de agregar al carrito
		if (!validarSelecciones()) {
			setError('Debe seleccionar opciones para todos los productos que las requieren');
			toast.error('Debe seleccionar opciones para todos los productos que las requieren');
			return;
		}

		// Crear array de productos con adicionales basado en productos expandidos
		const productosConAdicionales = productosExpandidos.map((productoExpandido) => ({
			...productoExpandido,
			adicionalSeleccionado: adicionalesSeleccionados[productoExpandido.key] || 0,
		}));

		// Crear pack con adicionales seleccionados
		const packConAdicionales = {
			...pack,
			productos: productosConAdicionales,
			precioConAdicionales: precioTotalConAdicionales,
		};

		handleAddToCart(packConAdicionales, cantidad, null, token, userId, 'pack', dispatch, cantidadProductosCarrito)
			.then(({ primerProductoAgregado }) => {
				toast.success('Pack agregado al carrito');
				if (primerProductoAgregado) {
					toast.info('Tu carrito se guarda por 24 horas. Luego se limpiará automáticamente.');
				}
			})
			.catch((e) => {
				toast.error(e.message);
			});
	};

	const incrementarCantidad = () => {
		setCantidad((prevCantidad) => prevCantidad + 1);
	};

	const decrementarCantidad = () => {
		setCantidad((prevCantidad) => (prevCantidad > 1 ? prevCantidad - 1 : 1));
	};

	const handleCantidadChange = (e) => {
		const value = parseInt(e.target.value, 10);
		setCantidad(isNaN(value) || value <= 0 ? 1 : value);
	};

	return (
		<Content>
			<Details>
				<Title>{pack.nombre || 'Pack sin título'}</Title>
				<Description>{pack.descripcion || 'Este pack no tiene descripción disponible.'}</Description>
				<PriceContainer>
					{ahorro > 0 && (
						<>
							<PriceLabel>Precio Original:</PriceLabel>
							<OriginalPrice>${precioCalculado.toFixed(2)}</OriginalPrice>
							<SavingsAmount>¡Ahorrás ${ahorro.toFixed(2)}!</SavingsAmount>
						</>
					)}
					<PriceLabel>Precio Final:</PriceLabel>
					<FinalPrice>
						${loading ? precioFinal.toFixed(2) : precioTotalConAdicionales.toFixed(2)}
						{!loading && precioTotalConAdicionales > precioFinal && (
							<PrecioAdicional>
								(+${(precioTotalConAdicionales - precioFinal).toFixed(2)} por adicionales)
							</PrecioAdicional>
						)}
					</FinalPrice>
				</PriceContainer>

				<ProductList>
					{loading ? (
						<p>Cargando opciones de productos...</p>
					) : productosExpandidos && productosExpandidos.length > 0 ? (
						productosExpandidos.map((producto, index) => {
							const adicionales = productosAdicionales[producto.id] || [];
							const seleccionado = adicionalesSeleccionados[producto.key];
							const tieneAdicionales = adicionales.length > 0;

							return (
								<ProductItem key={producto.key}>
									<ProductImage
										imageSrc={
											producto.imagen ||
											'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=500&q=80'
										}
									/>
									<ProductDetails>
										<ProductTitle>{producto.titulo}</ProductTitle>
										<ProductDescription>{producto.descripcion}</ProductDescription>
										{producto.mostrarCantidad && (
											<ProductQuantity>Cantidad: {producto.cantidad}</ProductQuantity>
										)}

										{tieneAdicionales && (
											<AdicionalesContainer>
												<SelectContainer>
													<SelectLabel>Selecciona una opción:</SelectLabel>
													<Select
														value={seleccionado || ''}
														onChange={(e) =>
															handleAdicionalChange(producto.key, e.target.value)
														}
														style={{
															borderColor: seleccionado === null ? '#ef4444' : '#d1d5db',
														}}
													>
														<option value="" disabled>
															Seleccionar opción
														</option>
														{adicionales.map((adicional) => (
															<option key={adicional.id} value={adicional.id}>
																{adicional.descripcion}
																{adicional.costoAdicional > 0 &&
																	` (+$${adicional.costoAdicional.toFixed(2)})`}
															</option>
														))}
													</Select>
												</SelectContainer>
											</AdicionalesContainer>
										)}
									</ProductDetails>
								</ProductItem>
							);
						})
					) : (
						<p>No hay productos en este pack.</p>
					)}
				</ProductList>

				{error && <ErrorMessage>{error}</ErrorMessage>}

				<QuantityContainer>
					<QuantityButton onClick={decrementarCantidad}>-</QuantityButton>
					<QuantityInput type="number" value={cantidad} onChange={handleCantidadChange} min="1" />
					<QuantityButton onClick={incrementarCantidad}>+</QuantityButton>
				</QuantityContainer>
				<ButtonContainer>
					<PrimaryButton onClick={handleAgregarACarrito}>Agregar al carrito</PrimaryButton>
				</ButtonContainer>
			</Details>
		</Content>
	);
};

export default Pack;
